import React from "react";
import FormControl from "../../inputs/form_control";
import Select, { SelectProps } from "../../inputs/select";
import PromptWithSecondaryText from "./shared_components/prompt_with_secondary_text";
import { Overrides } from "baseui/overrides";
import nextId from "react-id-generator";
import { Option, SelectOverrides, Value } from "baseui/select";
import InputQuestionWrapper from "./input_question_wrapper";
import Radio, { RadioGroup, RadioProps } from "../../inputs/radio";
import styled from "styled-components";
import baseuiTheme from "../../../../styling/baseui_theme";
import { InputOverrides } from "baseui/input";
import { FormControlOverrides } from "baseui/form-control";
import { useConstructAriaLabel } from "./use_construct_aria_label";
import { getFormControlLabel } from "./shared_components/shared_form_control_label";
import { captureError } from "../../../../../utils/capture_error";

const SELECT_NAMESPACE = "single-select-input";

// marginbottom prop is not camel cased to avoid this console warning:
// Warning: React does not recognize the `marginBottom` prop on a DOM element.
const StyledPromptWithSecondaryTextWrapper = styled.div<{ marginbottom?: string }>`
  margin-bottom: ${({ marginbottom }) => marginbottom};
`;

const StyledFollowUpWrapper = styled.div<{ marginTop?: string }>`
  margin-top: ${({ marginTop }) => marginTop};
`;

// Normalize some of the interface between Select and Radio, which have
// slightly different types in the Base library
export interface SelectQuestionWrapperValue {
  id: string;
  label: string;
}
// First, create a type from the shared prop names between Select and Radio
interface PickRadioSelectCombinedProps
  extends Pick<SelectProps | RadioProps, keyof SelectProps & keyof RadioProps> {}
// Next, omit these shared props so we can normalize their types below
interface OmitRadioSelectCombinedProps
  extends Omit<PickRadioSelectCombinedProps, "value" | "options" | "onChange"> {}
// Finally, normalize these types so that they can be used interchangeably
export interface RadioSelectCombinedProps extends OmitRadioSelectCombinedProps {
  options: { label: string; id: string }[];
  value?: SelectQuestionWrapperValue | Option;
  onChange: (value: SelectQuestionWrapperValue | Option) => void;
}

export interface SelectQuestionWrapperProps extends RadioSelectCombinedProps {
  supplementalQuestionPrompt?: string;
  secondaryText?: string;
  label?: string;
  optional: boolean;
  placeholder?: string;
  errorMessage?: string;
  showFollowUp?: boolean;
  followUpPrompt?: string;
  followUpIsOptional?: boolean;
  followUpCurrentCharacterCount?: number;
  followUpMaxCharacterCount?: number;
  followUpValue?: string;
  followUpErrorMessage?: string;
  followUpAriaLabel?: string; // Use this intentionally if the supplemental question prompt, secondary text, and label cannot be provided, such as in the case of a follow up input to an "other" option
  onChangeFollowUpValue?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  // A carry over from the current implementation, which allows the question component to tell the parent
  // component if it has an error or not. This is used to determine if the form can be submitted or not.
  setAbleToSubmit?: (ableToSubmit: boolean) => void;
  dataTest?: string;
}

const selectOverrides: Overrides<SelectOverrides> = {
  Root: {
    style: ({ $theme }) => ({
      marginTop: `-${$theme.sizing.scale500}`,
      maxWidth: "620px",
    }),
  },
};

const radioFormControlOverrides: Overrides<FormControlOverrides> = {
  Label: {
    style: () => {
      return {
        marginBottom: 0,
      };
    },
  },
};

const inputOverrides = (maxCharacters?: number): Overrides<InputOverrides> => ({
  Root: {
    style: ({ $theme }) => ({
      marginTop:
        // If we have a character counter, reduce the top margin.
        maxCharacters ? `0` : `${$theme.sizing.scale300}`,
      maxWidth: "620px",
    }),
  },
});

const RADIO_VARIATION_CUTOFF = 5;

const SelectQuestionWrapper = ({
  supplementalQuestionPrompt,
  secondaryText, // customizations.description
  label, //customizations.prompt
  value, //currentAnswer
  optional, //customizations.optional
  placeholder,
  errorMessage,
  showFollowUp,

  followUpPrompt, // customizations.followUp.prompt
  followUpIsOptional = false, // customizations.followUp?.optional,
  followUpCurrentCharacterCount,
  followUpMaxCharacterCount,
  followUpValue,
  followUpErrorMessage,
  followUpAriaLabel,
  onChangeFollowUpValue,
  setAbleToSubmit,
  dataTest = "select-question-wrapper",
  ...selectProps
}: SelectQuestionWrapperProps) => {
  const { constructAriaLabel } = useConstructAriaLabel();

  if (!label && !supplementalQuestionPrompt) {
    captureError(
      "Cannot render question: SelectQuestionWrapper must have a label or supplementalQuestionPrompt but found neither",
    );
    return null;
  }

  const selectId = nextId(SELECT_NAMESPACE);

  const { options } = selectProps;

  return (
    <div>
      {supplementalQuestionPrompt && (
        <StyledPromptWithSecondaryTextWrapper
          marginbottom={
            !label && options.length > RADIO_VARIATION_CUTOFF
              ? baseuiTheme.sizing.scale650
              : !label && !!secondaryText && options.length <= RADIO_VARIATION_CUTOFF
                ? `-${baseuiTheme.sizing.scale400}`
                : undefined
          }
        >
          <PromptWithSecondaryText
            supplementalQuestionPrompt={supplementalQuestionPrompt}
            secondaryText={secondaryText}
            optional={optional}
          />
        </StyledPromptWithSecondaryTextWrapper>
      )}
      {(options?.length as number) > RADIO_VARIATION_CUTOFF && (
        <FormControl
          label={getFormControlLabel(
            supplementalQuestionPrompt,
            label,
            optional,
            secondaryText,
            dataTest,
          )}
          htmlFor={selectId}
          error={errorMessage}
          dataTest={dataTest}
        >
          <Select
            id={selectId}
            {...(selectProps as unknown as SelectProps)}
            placeholder={placeholder}
            error={!!errorMessage}
            aria-label={constructAriaLabel(
              supplementalQuestionPrompt,
              secondaryText,
              label,
              optional,
            )}
            // Even though this is single select, for some reason Base shows the placeholder alongside the
            // selected value if the value provided here is not an array?
            value={value ? ([value] as Value) : undefined}
            onChange={(params) => selectProps.onChange(params.value[0])}
            overrides={selectOverrides}
            searchable
            clearable
            dataTest={dataTest}
          />
        </FormControl>
      )}
      {(options?.length as number) <= RADIO_VARIATION_CUTOFF && (
        <FormControl
          label={getFormControlLabel(
            supplementalQuestionPrompt,
            label,
            optional,
            secondaryText,
            dataTest,
          )}
          htmlFor={selectId}
          error={errorMessage}
          overrides={radioFormControlOverrides}
          dataTest={`${dataTest}-form-control`}
        >
          <RadioGroup
            id={selectId}
            aria-label={constructAriaLabel(
              supplementalQuestionPrompt,
              secondaryText,
              label,
              optional,
            )}
            {...(selectProps as unknown as RadioProps)}
            onChange={(e) =>
              selectProps.onChange({
                id: e.target.value,
                label: options.find((option) => option.id === e.target.value)?.label ?? "",
              })
            }
            value={(value as SelectQuestionWrapperValue)?.id}
            error={!!errorMessage}
            overrides={undefined}
          >
            {options.map((option, idx) => (
              <Radio
                value={option.id}
                key={option.id}
                data-test={`${dataTest}-radio-option-${idx + 1}`}
              >
                <span data-test={`${dataTest}-radio-option-${option.id}`}>{option.label}</span>
              </Radio>
            ))}
          </RadioGroup>
        </FormControl>
      )}
      {showFollowUp && (
        <StyledFollowUpWrapper
          marginTop={
            !followUpPrompt && options.length <= RADIO_VARIATION_CUTOFF
              ? `-${baseuiTheme.sizing.scale800}`
              : options.length > RADIO_VARIATION_CUTOFF
                ? baseuiTheme.sizing.scale750
                : undefined
          }
        >
          <InputQuestionWrapper
            label={followUpPrompt}
            optional={!!followUpIsOptional}
            value={followUpValue}
            onChange={onChangeFollowUpValue}
            currentCharacterCount={followUpCurrentCharacterCount}
            maxCharacterCount={followUpMaxCharacterCount}
            errorMessage={followUpErrorMessage}
            ariaLabel={followUpAriaLabel}
            overrides={inputOverrides(followUpMaxCharacterCount)}
            dataTest="select-follow-up-input"
          />
        </StyledFollowUpWrapper>
      )}
    </div>
  );
};

export default SelectQuestionWrapper;
